import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { auditTheme } from "../../theme/auditTheme";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { AuditButton } from "./AuditButton";
import { SurfaceCard } from "./SurfaceCard";

interface Props {
  /** The "View Add-In data" clearance the audit needs. */
  clearance: string;
  hasPermission: boolean;
  isLoading: boolean;
  /** The clearances have loaded in this session, so `hasPermission` is a real decision. */
  isResolved: boolean;
  isError: boolean;
  /** A reload of the clearances is running (the retry was pressed). */
  isRetrying?: boolean;
  onRetry: () => void;
  children: ReactNode;
}

const frameSx = {
  p: { xs: "14px 12px", sm: "20px 24px" },
  fontFamily: AUDIT_TOKENS.fontFamily,
  color: AUDIT_TOKENS.text,
} as const;

/**
 * Shows the page only to users with the clearance. "No permission" is shown only when the check
 * resolved to denied; a check that could not load says so, with a retry, because telling a user
 * with the clearance that they lack it would send them to the administrator for nothing.
 */
export function AuditAccessGate({ clearance, hasPermission, isLoading, isResolved, isError, isRetrying, onRetry, children }: Props) {
  const { t } = useTranslation();
  if (isLoading) return null;

  if (!isResolved && isError) {
    return (
      <ThemeProvider theme={auditTheme}>
        <Box sx={frameSx}>
          <Alert severity="error" sx={{ maxWidth: 720 }} action={<AuditButton onClick={onRetry} disabled={isRetrying}>
                {t(isRetrying ? "logbookAudit.clearance.retrying" : "logbookAudit.clearance.retry")}
              </AuditButton>}>
            <AlertTitle>{t("logbookAudit.clearance.errorTitle")}</AlertTitle>
            {t("logbookAudit.clearance.errorBody")}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  if (!hasPermission) {
    return (
      <ThemeProvider theme={auditTheme}>
        <Box role="alert" sx={frameSx}>
          <SurfaceCard sx={{ p: "20px 24px", maxWidth: 720 }}>
            <Box component="h2" sx={{ m: "0 0 8px", fontSize: 17, color: AUDIT_TOKENS.ink }}>
              {t("logbookAudit.clearance.title")}
            </Box>
            <Box sx={{ fontSize: 14, lineHeight: 1.55 }}>{t("logbookAudit.clearance.body", { clearance })}</Box>
          </SurfaceCard>
        </Box>
      </ThemeProvider>
    );
  }

  return <>{children}</>;
}
